import PropTypes from "prop-types";
import { Swiper, SwiperSlide } from "swiper/react";

import { useEffect } from "react";
import { withALErrorBoundary } from "../helpers/ErrorBoundary/ALErrorBoundary";

import { useCurrentCountryCode } from "@hooks/usePrices";
import useCart from "../hooks/useCart";
import useApplyLuxeDiscountToProductPrice from "@hooks/useApplyLuxeDiscountToProductPrice";
import { applyIgPrices } from "@helpers/intelligems";
import { cn } from "@helpers/general";
import { getProductPricesForIntelligems, isProductPreOrder } from "@helpers/product";

import { ALTag } from "./ALComponents";
import ALShopifyImage from "./al_components/ALShopifyImage";
import PriceContainer from "./PriceContainer";
import QuickAddToCart from "@components/QuickAddToCart";

import { Country } from "../constants/Country";
import { ComponentType } from "../constants/ComponentType";

import * as Styles from "./productElement/styles.module.scss";

function ProductElement({
  product,
  isSelected = false,
  onToggle,
  componentType = "",
  pricesLoading,
  igProductPrices = {},
  setIgProductPrices,
}) {
  const applyLuxeDiscountToProductPrice = useApplyLuxeDiscountToProductPrice();
  const countryCode = useCurrentCountryCode();
  const { quickAddToCart } = useCart(product);

  const pricesForIntelligems = getProductPricesForIntelligems(product);
  const igPrices = applyIgPrices(
    product?.shopify.id,
    product?.shopify.variants[0],
    pricesForIntelligems
  );

  useEffect(() => {
    if (igPrices && setIgProductPrices && !igProductPrices[product.handle] && !pricesLoading) {
      setIgProductPrices((prevState) => ({ ...prevState, [product.handle]: igPrices }));
    }
  }, [igPrices, setIgProductPrices, product?.handle, igProductPrices, pricesLoading]);

  if (!product) {
    return null;
  }

  const isPreOrder = isProductPreOrder(product);
  const hasNoVariantsAvailable = product.node.variants?.every(
    (x) => x.availableForSale === true && x.inventoryPolicy !== "DENY"
  );
  const shouldDisplayPreOrder = isPreOrder && countryCode === Country.US && hasNoVariantsAvailable;

  const images = (product.node?.images || []).slice(0, 2);

  const onProductClick = () => {
    if (onToggle) {
      onToggle(product.node.handle, {
        ...product,
        priceLuxe: applyLuxeDiscountToProductPrice(product?.finalPrice, product.node.tags),
      });
    }
  };

  const imagesElement = images ? (
    <>
      <div className={cn(Styles.desktopImage, "aspect-[0.8]")}>
        {images.map((image, i) => (
          <ALShopifyImage
            key={image?.url}
            url={image?.url}
            width="100%"
            height="100%"
            alt={image?.altText}
            className={i === 0 ? "" : Styles.hoverImage}
          />
        ))}
      </div>

      <div className={Styles.mobileImage}>
        <Swiper
          className="h-full"
          spaceBetween={0}
          slidesPerView={1}
          slidesPerGroup={1}
          loop
          pagination={{
            clickable: true,
            modifierClass: "product_element_pagination",
            bulletClass: "product_element_bullet",
            bulletActiveClass: "product_element_bullet--active",
          }}
        >
          {images.map((image) => (
            <SwiperSlide key={image?.url}>
              <ALShopifyImage
                url={image?.url}
                alt={image?.altText}
                imageClassName="w-full h-full"
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </>
  ) : null;

  return (
    <div className={Styles.container} onClick={onProductClick} data-testid="product-element">
      <div className={Styles.imageContainer}>
        <ALTag
          product={product}
          componentType={ComponentType.PRODUCT_RECOMMENDATIONS}
          shouldDisplayPreOrder={shouldDisplayPreOrder}
        />
        {componentType === ComponentType.PRODUCT_RECOMMENDATIONS && (
          <a
            key={product.id}
            href={`/products/${product.handle}?origin_content=shop-the-look-${product.handle}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {imagesElement}
          </a>
        )}
        {componentType !== ComponentType.PRODUCT_RECOMMENDATIONS && imagesElement}
        {componentType !== ComponentType.PRODUCT_RECOMMENDATIONS && (
          <input className={Styles.checkbox} type="checkbox" checked={isSelected} readOnly />
        )}
      </div>

      <div>
        <div className="flex flex-col mb-[15px]">
          <h4 className="typo-body font-bold m-0 mt-[15px] mobile:mt-[10px] pr-[5px]">
            {product.node.title.split(" - ")[1]}
          </h4>
          <p className="typo-micro text-dark-grey mb-[10px] mobile:mb-[8px]">
            {product.node.title.split(" - ")[0]}
          </p>
          <PriceContainer
            productId={product.node.id}
            prices={product} // TO DO: Refactor in ticket 11022739566
            pricesLoading={pricesLoading}
            tags={product.node.tags}
            componentType={componentType}
          />
        </div>

        {componentType === ComponentType.PRODUCT_RECOMMENDATIONS && (
          <QuickAddToCart
            product={product.shopify}
            onQuickAddToCart={quickAddToCart}
            componentType={ComponentType.PRODUCT_RECOMMENDATIONS}
          />
        )}
      </div>
    </div>
  );
}

ProductElement.propTypes = {
  product: PropTypes.shape({
    handle: PropTypes.string,
    contentful: PropTypes.shape({
      productVariants: PropTypes.arrayOf(
        PropTypes.shape({
          sku: PropTypes.string,
          isPreOrder: PropTypes.bool,
          preOrderStartDate: PropTypes.string,
          preOrderEndDate: PropTypes.string,
        })
      ),
    }),
    node: PropTypes.shape({
      handle: PropTypes.string,
      id: PropTypes.string,
      title: PropTypes.string,
      images: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string,
          altText: PropTypes.string,
        })
      ),
      tags: PropTypes.arrayOf(PropTypes.string),
      variants: PropTypes.arrayOf(
        PropTypes.shape({
          availableForSale: PropTypes.bool,
          inventoryPolicy: PropTypes.string,
        })
      ),
    }),
    shopify: PropTypes.shape({
      handle: PropTypes.string,
    }),
  }),
  isSelected: PropTypes.bool,
  onToggle: PropTypes.func,
  componentType: PropTypes.string,
  pricesLoading: PropTypes.bool,
  igProductPrices: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({}),
  }),
  setIgProductPrices: PropTypes.func,
};

export default withALErrorBoundary({
  name: "ProductElement",
  priority: "P3",
})(ProductElement);
